import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorBoundary } from "../ErrorBoundary";
import { ProjectQuestions, emptyAnswers, isEmpty } from "../ProjectQuestions";
import { Eyebrow, Panel, PanelPrimary } from "../SignIn";
import { TeamNameForm } from "../TeamName";
import {
  ApiError, APPLY_SIGN_IN_URL, applyCohort, applyLogout, applyMe, joinDemoCohort,
  putApplication,
  putProjectIntent, renameMyTeam, setMyDiscord, setMyHandle, withdrawApplication,
  type ApplicationAnswers, type ApplyCohortView, type ApplyMe, type ApplyProfileCohort,
  type IntakeQuestion,
  type ApplyProjectBody, type ApplyWork, type FormState,
  type ProfileData, type ProjectQuestion, type Social,
} from "../api";
import { useAsync } from "../useAsync";
import {
  Badge, Card, CardBody, CardHead, ErrorBox, Markdown, Skeleton, ThemeToggle, When,
} from "../ui";

/**
 * The student surface. Everything a person who is not a teacher can reach.
 *
 * 🔴 THIS PAGE MUST NOT CALL `getMe()`. That is the TEACHER endpoint, and it is the first
 * thing the console does on boot — so before this existed, a student who signed in perfectly
 * landed here, got a 401 from an endpoint meant for somebody else, and was shown a card
 * saying "sign in with the Google account that teaches the cohort". Clicking it sent them
 * down the teacher flow and refused them by name. `App` branches on the path before it boots
 * the teacher shell, and this file only ever talks to `/api/apply/*`.
 *
 * 🔴 AND IT SHOWS NO JUDGEMENT, BECAUSE IT IS NEVER SENT ANY. There is no `level`, no
 * rationale and no teammate email in `ApplyCohortView` — the server builds that payload key by
 * key and a test walks it for exactly these names. A page cannot render what it was never
 * given, which is the point of drawing the boundary at the server rather than here.
 */
export function ApplyPage() {
  const { courseId, round } = useParams();
  const me = useAsync(() => applyMe(), []);

  // The sign-in callback sends a student here with a reason when it refuses them.
  const [signinError] = useState(() =>
    new URLSearchParams(window.location.search).get("signin_error"));
  useEffect(() => {
    if (signinError) window.history.replaceState({}, "", window.location.pathname);
  }, [signinError]);

  if (me.loading && !me.data) {
    return <ApplyShell><div className="apply-pad"><Skeleton rows={3} /></div></ApplyShell>;
  }

  // 🔴 A 401 HERE MEANS "SIGN IN", NOT "SOMETHING BROKE". It is the ordinary state for a
  // person arriving from a link, so it gets the front door rather than an error box.
  if (signinError || me.error?.kind === "auth" || !me.data) {
    // 🔴 THE ROUTE KNOWS WHICH ROUND THIS IS, AND THE SCREEN NOW SAYS SO. One sign-in card
    // served every entrance, so a student sent the TEAMS link — somebody already enrolled,
    // who only needs to answer the project form — was offered "Apply to a cohort". `round`
    // has been a route param all along; nothing new is fetched to tell them apart.
    const project = round === "project";
    return (
      <ApplyShell>
        <div className="apply-pad">
        <Panel sent={project}>
          <div>
            {project
              ? <Eyebrow tag="/project">Project preferences</Eyebrow>
              : <Eyebrow>{`/apply${courseId ? `/${courseId}` : ""}`}</Eyebrow>}
            {/* ⚠️ THE COHORT'S NAME BELONGS HERE AND CANNOT REACH IT. The handoff asks for
                the cohort name as this heading, its `intro` as the lede and its closing date
                as the fine print — and all three live behind `GET /api/apply/{id}`, which
                requires `CurrentApplicant`. [X] Every data route on that router does; only
                `/auth/start` is public. So this screen is built from what the ROUTE knows.
                Closing it would take a public endpoint returning `{name, intro, closes_at}`
                for a cohort id — deliberately not added here, because this codebase refuses
                to let an unauthenticated caller turn an id into a cohort's name, and that is
                a decision for the owner rather than a side effect of a restyle. */}
            <h2>{project ? "Project preferences" : "Apply"}</h2>
          </div>
          <p className="panel-lede">
            {project
              ? "Answer this cohort's project form so your teacher can group you into a team."
              : "Sign in with Google to apply to a cohort, or to see an application you have "
                + "already sent."}
          </p>
          {/* 🔴 THE SENTENCE THAT PRE-EMPTS THE REFUSAL. Roster matching is exact on the
              Google account and never on a name; saying so BEFORE sign-in costs nothing and
              saves the reader a refusal they cannot act on afterwards. `ProjectForm`'s
              wording, verbatim. */}
          {project && (
            <p className="panel-note">
              Sign in with the Google account you are enrolled on this cohort with. You are
              matched to the roster by that account, never by name.
            </p>)}
          {signinError && (
            <div className="panel-refused" role="alert">
              <span className="tag">REFUSED</span>
              <p className="t">{signinError}</p>
            </div>)}
          {me.error && me.error.kind !== "auth" && <ErrorBox err={me.error} onRetry={me.reload} />}
          <PanelPrimary href={APPLY_SIGN_IN_URL(courseId ?? "")}>
            {signinError ? "Try again" : "Continue with Google"}
          </PanelPrimary>
          {/* ⚠️ NOT THE CONSOLE'S FINE PRINT. The teacher's adds a clause about the bot
              credential reading Classroom; a student has nothing to do with it. Two surfaces,
              two strings, deliberately not shared. */}
          <p className="panel-fine">
            Signing in asks only for your name and email address. It does not give this
            console access to anything else in your Google account.
          </p>
        </Panel>
        </div>
      </ApplyShell>
    );
  }

  return (
    <ApplyShell who={me.data} onSignOut={() => void applyLogout().then(me.reload)}>
      {courseId
        ? <Cohort courseId={courseId} round={round} email={me.data.email} />
        : <Profile me={me.data} onChanged={me.reload} />}
    </ApplyShell>
  );
}

/**
 * The student's own page: who they are, what is waiting, and their work.
 *
 * 🔴 IT REPLACED A CHOOSER, WHICH WAS THE WHOLE COMPLAINT. `/apply` used to list the forms
 * that were open and nothing else — so a student who had already applied, been grouped and
 * handed work in landed on a page about cohorts they could join. Everything this console knew
 * about them was on the teacher's side of the wall. The open forms have not gone: they are the
 * first announcement and they are the Forms tab.
 *
 * ⚠️ THE TABS ARE STATE AND NOT A URL, WHICH IS THE OPPOSITE OF THE CONSOLE'S RULE AND RIGHT
 * HERE. A teacher's page needs an address because an alarm has to point at a row and a link
 * goes in a message to somebody else; this page renders only for the person signed in, so
 * there is nobody a link could be sent to. `/apply/:courseId` also already means the form.
 */
export function Profile({ me, onChanged, readOnly = false }: {
  me: ProfileData; onChanged: () => void; readOnly?: boolean;
}) {
  const [which, setWhich] = useState(0);
  const [tab, setTab] = useState<"team" | "work" | "forms">("team");
  const cohort = me.cohorts[which] as ApplyProfileCohort | undefined;
  // 🔴 A COUNT ON A TAB IS A COUNT OF WHAT THE TAB SHOWS. [X] Both of these summed every
  // cohort while the tab under them renders ONE — so a cohort with nothing open read
  // "Forms 1" over the words "Nothing is open for this cohort at the moment". Seen in a
  // render, not in the source, because a number is right for somebody.
  const openHere = me.announcements.filter(
    (a) => a.kind === "form_open" && a.course_id === cohort?.id).length;
  const openForms = me.announcements.filter((a) => a.kind === "form_open").length;

  return (
    <div className="apply-pad stack">
      {/* 🔴 PROFILE LEFT, ANNOUNCEMENTS RIGHT, AND THE ORDER IS REVERSED ON A PHONE BY THE
          STYLESHEET, NOT HERE: what is waiting matters more than who you are, and on one
          column the card would otherwise push it below the fold. */}
      <div className="prof-top">
        <ProfileCard account={me.account} onChanged={onChanged} readOnly={readOnly} />
        <Announcements rows={me.announcements} />
      </div>

      {/* ⚠️ PILLS ONLY WHEN THERE IS MORE THAN ONE. A single pill is a control that decides
          nothing, and most students are on one cohort. */}
      {me.cohorts.length > 1 && (
        <div className="row gap wrap" role="tablist" aria-label="Your cohorts">
          {me.cohorts.map((c, i) => (
            <button key={c.id} type="button" className="pill" role="tab"
                    aria-selected={i === which} onClick={() => setWhich(i)}>
              {c.name}
            </button>))}
        </div>)}

      {me.cohorts.length === 0 ? (
        <Card>
          <CardHead title="You are not on a cohort yet"
                    desc="This page fills in once your teacher enrols you." />
          <CardBody>
            <div className="stack-tight">
            <p className="muted">
              {openForms > 0
                ? "There is an open form above. Answering it is how you ask to join."
                : "Nothing is open at the moment. If you were sent a link, ask whoever sent "
                  + "it whether applications have opened yet."}
            </p>
            {/* 🔴 THE CONSOLE'S REFUSAL, KEPT FOR THE ONE READER WHO NEEDS IT (2026-09-28). An
                address with no `teachers` row signing in on the front page now lands here
                instead of on a sentence naming it — right for a student, and for a teacher who
                picked the wrong one of their Google accounts that sentence was the only thing
                that said so. ⚠️ NEVER ON THE TEACHER'S PREVIEW (`readOnly`): it is somebody
                else's page, and must say nothing about whether that student teaches. */}
            {!readOnly && !me.is_teacher && (
              <p className="small muted">
                If you teach on this console, {me.account.email} is not the address you were
                added under. Sign out, then sign in on <a href="/">the console&rsquo;s front
                page</a> with that one.
              </p>)}
            </div>
          </CardBody>
        </Card>
      ) : cohort && (
        <>
          <div className="ptabs" role="tablist">
            {([["team", "Team"], ["work", "Assignments"], ["forms", "Forms"]] as const)
              .map(([k, label]) => (
                <button key={k} type="button" className="ptab" role="tab"
                        aria-selected={tab === k} onClick={() => setTab(k)}>
                  {label}
                  {k === "work" && cohort.work.length > 0 && (
                    <span className="nav-count">{cohort.work.length}</span>)}
                  {k === "forms" && openHere > 0 && (
                    <span className="nav-count">{openHere}</span>)}
                </button>))}
          </div>

          {tab === "team" && <MyTeam cohort={cohort} onChanged={onChanged} />}
          {tab === "work" && <MyWork rows={cohort.work} />}
          {tab === "forms" && (
            <MyForms cohort={cohort}
                     open={me.announcements.filter(
                       (a) => a.kind === "form_open" && a.course_id === cohort.id)} />)}
        </>
      )}
    </div>
  );
}

/** Who this is, and the two fields on the page that are theirs to set. */
function ProfileCard({ account, onChanged, readOnly }: {
  account: ProfileData["account"]; onChanged: () => void; readOnly: boolean;
}) {
  return (
    <Card>
      <CardBody>
        <div className="prof-id">
          <Avatar name={account.display_name || account.email} url={account.picture} />
          <div className="prof-who">
            <div className="prof-name">{account.display_name || account.email.split("@")[0]}</div>
            <div className="prof-mail">{account.email}</div>
          </div>
        </div>
        <dl className="prows">
          <HandleRow social={account.social} onChanged={onChanged}
                     readOnly={readOnly} />
          <DiscordRow discord={account.discord} onChanged={onChanged} readOnly={readOnly} />
        </dl>
      </CardBody>
    </Card>
  );
}

/** The Discord name their teammates reach them by — set here, on the card (2026-10-01).
 *
 * 🔴 IT WAS A LINK TO THEIR COHORT'S FORM, AND THAT FORM DID NOT ASK. The name lived on the
 * intake application alone, so Add pointed at `/apply/<cohort>` — and a running cohort has no
 * intake form, so the link opened the project round and the team. [X] On prod every signed-in
 * student of the Q3 2026 Builders Cohort, 99 of them, met exactly that. It is stored on their
 * account now and read first wherever a Discord name is shown, a teammate's view included.
 * ⚠️ NO REMOVE BUTTON. The name shown may be their intake answer, which stays theirs to change
 * on that form; clearing this box and saving is how a name set here is taken back.
 */
function DiscordRow({ discord, onChanged, readOnly }: {
  discord: string; onChanged: () => void; readOnly: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(discord);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await setMyDiscord(name);
      setEditing(false);
      onChanged();
    } catch (e) {
      // ⚠️ THE SERVER'S SENTENCE: `teams.discord_name` is the one rule, shared with the
      // intake form, and a client that restated it would drift from both.
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    // ⚠️ THE SAME SHAPE AS THE SOCIALS ROW ABOVE, full width while editing — the reason is
    // recorded there, and two rows of one kind must not look like two different things.
    <div className={editing ? "prow wide" : "prow"}>
      <dt className="k">Discord</dt>
      <dd className="v">
        {!editing ? (
          <span className="split">
            {/* ⚠️ A DASH ONLY FOR A READER WITH NO BUTTON — the teacher's preview, where an
                empty cell would read as a fault. Beside Add it would state the absence the
                button is there to end. */}
            <span>
              {discord || (readOnly ? <span className="muted">{"\u2014"}</span> : null)}
            </span>
            {/* 🔴 NOT IN A PREVIEW: a teacher typing a student's contact name from a page
                captioned "what they see" would be putting it in their mouth.
                ⚠️ ITS OWN ACCESSIBLE NAME, because the Socials row also says "Add" and a
                screen reader must hear which. */}
            {!readOnly && (
              <button className="btn sm split-side" type="button"
                      aria-label={`${discord ? "Change" : "Add"} your Discord username`}
                      onClick={() => { setName(discord); setEditing(true); }}>
                {discord ? "Change" : "Add"}
              </button>)}
          </span>
        ) : (
          <div className="stack tight">
            {/* ⚠️ `wide`, NOT `grow`. [X] Seen in a render: `.grow` is `flex: 1`, and in this
                column that is the HEIGHT — the 34px box collapsed to the height of its text.
                The Socials input gets away with it because it sits in a row. */}
            <input className="input wide" aria-label="Your Discord username" value={name}
                   placeholder="adabuilds" onChange={(e) => setName(e.target.value)} />
            {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
            <div className="row gap">
              <button className="btn primary sm" type="button" disabled={busy}
                      onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
              <button className="btn sm" type="button" disabled={busy}
                      onClick={() => { setEditing(false); setErr(null);
                                       setName(discord); }}>Cancel</button>
            </div>
            {/* ⚠️ WHO SEES IT, IN ONE LINE — a teammate does, and that is the whole point. */}
            <p className="small muted">
              Your teammates see this — it is how they reach you.
            </p>
          </div>
        )}
      </dd>
    </div>
  );
}

/** 🔴 INITIALS ARE A STATE, NOT A FALLBACK FOR A BROKEN IMAGE. A student who has never signed
 *  in has no picture at all — the teacher's preview meets that constantly — so the ring and
 *  the size are identical either way and the card does not move when a picture arrives.
 *  ⚠️ `referrerPolicy="no-referrer"`: the URL is on Google's host, so the browser fetches it
 *  from them; it need not also tell them which page of ours the student is reading. */
function Avatar({ name, url }: { name: string; url: string }) {
  const [broken, setBroken] = useState(false);
  const initials = name.trim().split(/\s+/).slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "").join("") || "?";
  if (!url || broken) {
    return <div className="avatar initials" aria-hidden="true">{initials}</div>;
  }
  return <img className="avatar" src={url} alt="" referrerPolicy="no-referrer"
              onError={() => setBroken(true)} />;
}

/** The handle their build log is read from — the one thing on this card that is theirs to set.
 *
 * 🔴 THE STRONGEST CONSENT RECORD THIS CONSOLE HAS. `models/grading.py` says associating an
 * external account with a named student is off-platform matching, permitted only with express
 * opt-in obtained before the association, and that a member of staff typing it in "does not
 * record that consent". Typed here, `added_by` is the student's own address.
 */
function HandleRow({ social, onChanged, readOnly }: {
  social: Social; onChanged: () => void; readOnly: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState(social.kind || "x");
  const [handle, setHandle] = useState(social.handle);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  // 🔴 THE VALUE IS AN ARGUMENT, NOT READ FROM STATE. Remove used to set the handle to "" and
  // call this in the same click — and this read the handle from the render it was made in, so
  // it saved the old handle again instead of clearing it.
  async function save(value: string) {
    setBusy(true);
    setErr(null);
    try {
      await setMyHandle(kind, value);
      setEditing(false);
      onChanged();
    } catch (e) {
      // ⚠️ THE SERVER'S SENTENCE, NOT A RULE RESTATED HERE. It names what is wrong with the
      // handle per kind, and a client that guessed would drift from what is enforced.
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    // ⚠️ THE ROW GOES FULL WIDTH WHILE IT IS BEING EDITED. [X] Seen in a render: inside the
    // 320px card the 78px label gutter left ~220px, so the two controls wrapped onto separate
    // lines and the sentence under them became a five-line column. A label above its own
    // fields is the ordinary form layout; the gutter is for a value being read.
    <div className={editing ? "prow wide" : "prow"}>
      {/* 🔴 NEVER "BUILD LOG" (owner, 2026-09-24: *"What the fuck is build log??"*). That is
          the CODE's name for the weekly sweep, and this project decided long ago that it
          reads as "Social activity" everywhere a person meets it — the nav, the Overview
          card, the roster column, the Health card, the settings page. It reached a STUDENT's
          own card, where it is worse than jargon: a student does not know this console
          sweeps anything, so the words name a feature they have never been told about.
          ⚠️ AND NOT "Social activity" EITHER, which is the teacher's noun for their own
          sweep. What this row holds is the account the student gave us. */}
      <dt className="k">Socials</dt>
      <dd className="v">
        {!editing ? (
          // ⚠️ `.split` ALONE, NEVER `.mate.split`. [X] Seen in a render: `.mate` carries a
          // border, a fill and 10px of padding, so this row came out as a boxed control
          // beside a Discord row that is plain text — two rows of the same kind looking
          // like two different things. `.mate` is the announcement card's chrome; the
          // layout is its own class.
          <span className="split">
            {/* ⚠️ NOTHING WHERE THERE IS NO HANDLE (owner, 2026-09-24). The button beside
                this already reads "Add", so "not set" was the same fact twice — and the one
                of the two that cannot be acted on was the one the eye met first. */}
            <span>
              {social.handle
                ? <>{LINK_KIND[social.kind] ?? social.kind} <strong>{social.handle}</strong></>
                : readOnly ? <span className="muted">{"\u2014"}</span> : null}
            </span>
            {/* 🔴 NOT IN A PREVIEW. `added_by` is the record of who asserted this link, and
                the whole reason the student's own field is worth having is that it names
                THEM. A teacher changing it from inside a page captioned "what they see"
                would write their own address into somebody's consent record. The teacher's
                own control is on the Students tab, where it says whose it is. */}
            {!readOnly && (
              <button className="btn sm split-side" type="button"
                      onClick={() => setEditing(true)}>
                {social.handle ? "Change" : "Add"}
              </button>)}
          </span>
        ) : (
          <div className="stack tight">
            <div className="row gap wrap">
              <select className="input" aria-label="Where you post"
                      value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="x">X</option>
                <option value="bluesky">Bluesky</option>
              </select>
              <input className="input grow" aria-label="Your handle" value={handle}
                     placeholder={kind === "bluesky" ? "alice.bsky.social" : "adabuilds"}
                     onChange={(e) => setHandle(e.target.value)} />
            </div>
            {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
            <div className="row gap">
              <button className="btn primary sm" type="button" disabled={busy}
                      onClick={() => void save(handle)}>{busy ? "Saving…" : "Save"}</button>
              <button className="btn sm" type="button" disabled={busy}
                      onClick={() => { setEditing(false); setErr(null);
                                       setHandle(social.handle);
                                       setKind(social.kind || "x"); }}>Cancel</button>
              {social.handle && (
                <button className="btn sm" type="button" disabled={busy}
                        onClick={() => { setHandle(""); void save(""); }}>Remove</button>)}
            </div>
            {/* ⚠️ WHAT IT IS FOR, IN ONE LINE. A field asking for a social handle with no
                reason beside it reads as this console collecting one because it can. */}
            <p className="small muted">
              Your teacher checks what you post publicly about your build. Only whether a post
              is about the programme is recorded — never what it said.
            </p>
          </div>
        )}
      </dd>
    </div>
  );
}

const LINK_KIND: Record<string, string> = { x: "X", bluesky: "Bluesky" };

/** 🔴 EVENTS WITH A DATE, NOT A LIST OF STATES (owner). "Your team was published" is news;
 *  "you have a team" is a dashboard nobody reads twice. Global rather than per cohort, each
 *  row naming its own — inside a tab, the only thing on this page with a deadline would be
 *  behind a click. */
function Announcements({ rows }: { rows: ApplyMe["announcements"] }) {
  return (
    <Card>
      <CardHead title="Announcements" desc="What has changed, and what is waiting for you." />
      <CardBody>
        {rows.length === 0 ? (
          <p className="muted">Nothing new. Anything your teacher publishes turns up here.</p>
        ) : (
          <ul className="anns">
            {rows.map((a, i) => <li key={i} className="ann"><Ann a={a} /></li>)}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function Ann({ a }: { a: ApplyMe["announcements"][number] }) {
  if (a.kind === "form_open") {
    const where = `/apply/${encodeURIComponent(a.course_id)}${
      a.round === "project" ? "/project" : ""}`;
    return (
      <span className="mate split">
        <span className="split-main">
          {/* 🔴 IT SAYS WHICH OF TWO CASES THE READER IS IN. "Waiting for you" is false for
              somebody who has already answered, and one label for both is wrong for half of
              them — the server sends `answered` for exactly this. */}
          <strong>{a.answered ? "You have answered" : "Waiting for you"}</strong>
          <span className="small">
            {a.round === "project" ? "Project form" : "Application form"} · {a.course_name}
            {a.closes_at && <> · closes <When iso={a.closes_at} /></>}
          </span>
        </span>
        <a className="btn sm split-side" href={where}>
          {a.answered ? "Review it" : "Answer it"}
        </a>
      </span>
    );
  }
  if (a.kind === "team_published") {
    return (
      <span className="mate split">
        <span className="split-main">
          <strong>Your team was published</strong>
          <span className="small">
            {a.team || (a.number ? `Team ${a.number}` : "Your team")} · {a.course_name}
            {a.mates.length > 0 && <> · with {a.mates.join(", ")}</>}
          </span>
        </span>
        <a className="btn sm split-side"
           href={`/apply/${encodeURIComponent(a.course_id)}/project`}>See it</a>
      </span>
    );
  }
  return (
    <span className="mate split">
      <span className="split-main">
        <strong>{a.title} was marked</strong>
        <span className="small">{a.course_name}</span>
      </span>
      <Mark points={a.points} max={a.max_points} />
    </span>
  );
}

/** A number, or nothing. 🔴 NEVER A ZERO STANDING IN FOR AN ABSENT MARK. */
function Mark({ points, max }: { points: number | null; max: number | null }) {
  if (points === null) return null;
  return (
    <span className="mark split-side">
      <strong>{points}</strong>{max !== null && <span className="small">/{max}</span>}
    </span>
  );
}

function MyTeam({ cohort, onChanged }: {
  cohort: ApplyProfileCohort; onChanged: () => void;
}) {
  if (cohort.team.status === "team_assigned" && cohort.team.team) {
    return <TeamCard team={cohort.team.team} courseId={cohort.id} onChanged={onChanged} />;
  }
  return (
    <Card>
      <CardHead title="Your team" desc={cohort.name} />
      <CardBody>
        {/* ⚠️ THREE STATES AND NONE OF THEM IS AN ERROR. "A plan exists and you are not in it"
            is a real state a student needs the words for, and it is not the same sentence as
            "nothing has been published yet". */}
        <p className="muted">
          {cohort.team.status === "not_assigned"
            ? "Teams have been published for this cohort and you are not in one. Ask your "
              + "teacher."
            : "No teams have been published for this cohort yet."}
        </p>
      </CardBody>
    </Card>
  );
}

/** What they handed in, and a mark only where one reached them. */
function MyWork({ rows }: { rows: ApplyWork[] }) {
  return (
    <Card>
      <CardHead title="Assignments"
                desc="What you have handed in, and your mark once your teacher has sent it." />
      <CardBody>
        {rows.length === 0 ? (
          <p className="muted">Nothing has been set for this cohort yet.</p>
        ) : (
          <ul className="worklist">
            {rows.map((w) => <li key={w.course_work_id} className="work"><Work w={w} /></li>)}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

const WORK_WORD: Record<ApplyWork["status"], string> = {
  marked: "marked",
  // 🔴 "BEING MARKED", NEVER A NUMBER. The console holds a grade nobody has sent; showing it
  // would tell a student they have a mark that does not exist yet.
  being_marked: "being marked",
  handed_in: "handed in",
  not_handed_in: "not handed in",
};

function Work({ w }: { w: ApplyWork }) {
  return (
    <>
      {/* ⚠️ `.split` ALONE: `.work` around it is already a bordered card, and `.mate` would
          draw a second border inside the first — invisible in dark, where both fills are
          `--surface`, and plainly a box-in-a-box in light. */}
      <div className="split">
        <span className="split-main">
          <strong>{w.title}</strong>
          <span className="small">
            <Badge tone={w.status === "marked" ? "ok"
                         : w.status === "not_handed_in" ? "plain" : "warn"}>
              {WORK_WORD[w.status]}
            </Badge>
            {w.due && <> · due {onDay(w.due)}</>}
            {w.handed_in_at && <> · handed in <When iso={w.handed_in_at} /></>}
          </span>
        </span>
        <Mark points={w.points} max={w.max_points} />
      </div>
      {/* ⚠️ THE NOTE WRITTEN FOR THEM, NEVER THE TEACHER'S OWN. The server sends one and not
          the other, and it blanks even this one where a misconduct or injection flag was
          raised — so a page cannot show what it was never given. */}
      {w.note && <p className="work-note">{w.note}</p>}
      {w.criteria.length > 0 && (
        <ul className="crits">
          {w.criteria.map((c, i) => (
            <li key={i} className="crit">
              <span className="small">{c.label}</span>
              <span className="small">
                {c.earned === null ? "—" : c.earned}{c.weight !== null && `/${c.weight}`}
              </span>
            </li>))}
        </ul>)}
    </>
  );
}

/** Which rounds this cohort is asking for, and whether this person has answered. */
function MyForms({ cohort, open }: {
  cohort: ApplyProfileCohort; open: ApplyMe["announcements"];
}) {
  const here = open;
  const applied = cohort.applied;
  return (
    <Card>
      <CardHead title="Forms" desc={cohort.name} />
      <CardBody>
        <ul className="anns">
          {here.map((a, i) => <li key={i} className="ann"><Ann a={a} /></li>)}
          {here.length === 0 && (
            <li className="ann">
              <span className="muted">Nothing is open for this cohort at the moment.</span>
            </li>)}
          {applied && (
            <li className="ann">
              <span className="mate split">
                <span className="split-main">
                  <strong>Your application</strong>
                  <span className="small">
                    {applied.withdrawn ? "withdrawn" : "sent"} · {cohort.name}
                  </span>
                </span>
                <a className="btn sm split-side"
                   href={`/apply/${encodeURIComponent(cohort.id)}`}>Open it</a>
              </span>
            </li>)}
        </ul>
      </CardBody>
    </Card>
  );
}

/** One cohort: the form, this person's answers, and their team once it is published. */
function Cohort({ courseId, round, email }:
  { courseId: string; round?: string; email: string }) {
  const q = useAsync(() => applyCohort(courseId), [courseId]);

  if (q.loading && !q.data) return <div className="apply-pad"><Skeleton rows={5} /></div>;
  if (q.error) {
    // ⚠️ A 404 HERE IS THE SERVER'S ONE SENTENCE FOR EVERY COHORT THIS PERSON MAY NOT SEE —
    // closed, never opened, or not a cohort at all. Rendering it as prose rather than as a
    // fault is right: nothing is broken, and there is nothing for them to retry.
    if (q.error.status === 404) {
      return <div className="apply-pad"><Card><CardBody>
        <p className="alert info" role="status">{q.error.message}</p>
      </CardBody></Card></div>;
    }
    return <div className="apply-pad"><ErrorBox err={q.error} onRetry={q.reload} /></div>;
  }
  if (!q.data) return null;

  // 🔴 A COHORT THAT IS ALREADY RUNNING HAS NO APPLICATION FORM, and the page has to render
  // anyway. `form === null` is not an error state: it is the ordinary shape of the second
  // round, and treating it as one is how a legitimately enrolled student saw nothing.
  const d = q.data;
  // 🔴 `/apply/<id>/project` IS THE LINK A TEACHER SENDS FOR THIS ROUND. The bare path shows
  // whichever rounds are open, which is right for a student who was simply sent "the link"
  // — and wrong for anybody who meant a particular one, because with the project form off
  // that page renders the INTAKE questions and nothing else.
  if (round === "project" || d.form === null) {
    return <ProjectOnly view={d} courseId={courseId} email={email}
                        onChanged={q.reload} />;
  }
  // ⚠️ SPREAD RATHER THAN CAST. `d.form` is narrowed by the return above, so rebuilding the
  // object hands `CohortView` a type that says what the guard already established — no
  // assertion, and no way for the guard to be removed without this failing to compile.
  return <CohortView view={{ ...d, form: d.form }} courseId={courseId} email={email}
                     onChanged={q.reload} />;
}

/** A running cohort: no application form, just the project questions and the team. */
function ProjectOnly({ view, courseId, email, onChanged }: {
  view: ApplyCohortView; courseId: string; email: string; onChanged: () => void;
}) {
  const p = view.project;
  return (
    <div className="stack-flush">
      {/* 🔴 THE PAGE HERO IS GONE, AND IT WAS THE "TWO HEADINGS" DEFECT WEARING A DIFFERENT
          HAT (owner, 2026-09-09). It printed the cohort's name in a full-bleed band, and the
          form card beneath it — since handoff `8a` — prints the same name as its own `h1`. So
          the screen said "Turbine-test" twice, once in each of two headings, which is exactly
          what deleting "Your project" was meant to fix.
          🔴 IT WAS ALSO THE BLACK STRIP IN LIGHT MODE. `.apply-hero` is `--brand-band`, dark
          in BOTH themes — right for the wordmark band above it, which has no dark-on-light
          logo and must stay dark, and wrong for a heading, which has no such constraint. With
          the hero gone the dark region is the brand band alone and everything below follows
          the toggle, which is what light mode is for. */}
      <div className="apply-body solo">
        <div className="stack">
          {/* 🔴 IT SAYS SO WHEN THERE IS NOTHING TO FILL IN. `ProjectSection` renders nothing
              when the form is shut and this person has neither an answer nor a team — right
              inside a page that has other cards, and a blank screen on a page that does not.
              Somebody sent this link before it opened needs a sentence, not an empty page. */}
          {p === null || (p.form.state !== "open" && !p.intent && !p.team) ? (
            <Card><CardBody>
              <p className="alert info" role="status">
                {p !== null && p.form.state === "closed"
                  ? "Project preferences for this cohort have closed."
                  : "This cohort is not collecting project preferences yet. If you were "
                    + "sent this link, ask whoever sent it whether it has opened."}
              </p>
            </CardBody></Card>
          ) : (
            <ProjectSection p={p} courseId={courseId} email={email} demo={view.demo}
                            onChanged={onChanged} />)}
        </div>
      </div>
    </div>
  );
}

/** A date a student reads, not a timestamp. "1 October" — no year, no clock. */
function onDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "long" });
}

/**
 * A deadline: the day, the clock time, and the zone it is being shown in.
 *
 * 🔴 A DATE ALONE IS A DIFFERENT DAY IN HALF THE WORLD. The pill said `CLOSES SEPTEMBER 11`
 * for a deadline stored as 23:59 UTC — which is the 12th at 07:59 in Singapore and the 11th
 * at 19:59 in New York. This programme asks every applicant for their time zone and groups
 * people by it, so its own deadline may not be the one thing on the page that pretends
 * everybody shares a calendar.
 *
 * 🔴 THE ZONE NAMED IS THE VIEWER'S, BECAUSE THAT IS THE ONE BEING RENDERED. `closes_at` is
 * always zone-aware on the wire (`projects.deadline` coerces a naive value to UTC, as the
 * intake round has always done), so `toLocaleString` converts correctly — and labelling that
 * result "UTC" would be a false statement about a number the reader is looking at.
 *
 * ⚠️ BUILT FROM TWO PARTS, NOT ONE `toLocaleString`. A single call renders the locale's own
 * joiner — "11 September at 23:59 WEST" — and the pill upper-cases its label, which turns
 * that into a shouted "AT". The separator has to be ours for the pill to read.
 */
function closesLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // ⚠️ A SHORT MONTH, WHICH IS ALSO WHAT `ui.when` USES FOR EVERY OTHER TIMESTAMP HERE.
  // [X] "SEPTEMBER" costs 46px in the pill and a 360px handset has 270px of room, so the
  // long form wrapped a 999px pill onto three lines. "12 Sep" loses nothing a deadline needs.
  const day = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  // 🔴 A 24-HOUR CLOCK, AGAINST THE READER'S LOCALE, AND ONLY HERE. [X] Under `en-US`,
  // `hour: "2-digit"` renders midnight as "12:59 AM" — the one time of day people reliably
  // read backwards, on the one string where being an hour out means the form is shut. The
  // date and the zone still follow the locale; only the clock is pinned.
  const time = d.toLocaleTimeString(undefined,
    { hour: "2-digit", minute: "2-digit", hour12: false, timeZoneName: "short" });
  return `${day}, ${time}`;
}

/**
 * The project round's questions, this person's answers, and their team once published.
 *
 * 🔴 THE ELIGIBILITY SENTENCE COMES FIRST AND REPLACES THE FORM. There are four reasons
 * somebody cannot answer and three of them are nothing to do with them — a missing scope, a
 * roster nobody has synced, a roster Classroom returned no names for. Letting them type a
 * paragraph into a form that was always going to refuse it is the cruel version of the same
 * refusal.
 *
 * 🔴 WHAT LOCKS AFTER PUBLICATION IS DECIDED PER QUESTION, AND THIS RECONCILES A DISAGREEMENT
 * THE CODE HAD WRITTEN DOWN AND DEFERRED. `projects.upsert_intent` refuses an edit after teams
 * are published — or after the round closes — only when a GROUPING answer changes; an answer
 * the model never read may still be corrected. The client refused all of them, so it was
 * stricter than the server, which is the safe direction but is not the same thing as agreeing.
 * `frozen()` below is the server's rule, spelled once.
 *
 * ⚠️ AND IT IS DATA, NOT A NAMED QUESTION. The handoff says "only the notes question stays
 * editable"; what stays editable is every question carrying `grouping: false`, which a teacher
 * sets per question. In the programme's shipped seven that is Supporting links — the CV — and
 * Additional notes is a grouping input. Naming a question here would have been wrong twice.
 */
function ProjectSection({ p, courseId, email, demo, onChanged }: {
  p: NonNullable<ApplyCohortView["project"]>; courseId: string; email: string;
  /** Whether this cohort is one this console invented — see `NotEligible`. */
  demo: boolean;
  onChanged: () => void;
}) {
  const { form, intent, status, team, eligibility } = p;
  // ⚠️ EVERY QUESTION PRESENT, so a control is never uncontrolled — and the stored answers
  // laid over it, so a question added since somebody answered starts empty rather than
  // undefined. Keys the form no longer has are dropped: the server refuses them by name.
  const [draft, setDraft] = useState<ApplyProjectBody>(() =>
    ({ ...emptyAnswers(form), ...(intent ? intent.answers : {}) }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);

  const published = team !== null || status === "team_assigned" || status === "not_assigned";
  const closed = form.state !== "open";
  // 🔴 THE SERVER'S RULE, ONCE. Not open, or teams published: an existing answer may still be
  // corrected where the model never read it, and somebody with no row at all cannot start.
  const restricted = closed || published;
  const mayWrite = !restricted || intent !== null;
  // ⚠️ "WHAT THE GROUPING READS" IS WIDER THAN THE MODEL: the server locks every answer the
  // arrangement reads — a grouping question, an axis, the time zone, a pick — and so does this.
  const readByGrouping = (q: ProjectQuestion) =>
    q.grouping !== false || Boolean(q.axis) || q.kind === "timezone" || q.kind === "teammates";
  const frozen = (q: ProjectQuestion) => !mayWrite || (restricted && readByGrouping(q));
  const why = (q: ProjectQuestion) =>
    !frozen(q) ? ""
      : published ? "Teams are out, so this answer is fixed."
        : closed ? "This round has closed, so this answer is fixed."
          : "";

  const set = (id: string, v: string | string[]) => {
    setDraft((d) => ({ ...d, [id]: v }));
    setSaved(false);
  };

  const send = async () => {
    setBusy(true); setErr(null);
    try { await putProjectIntent(courseId, draft); setSaved(true); onChanged(); }
    catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  };

  if (!eligibility.ok) {
    return <NotEligible eligibility={eligibility} courseId={courseId} email={email}
                        demo={demo} onJoined={onChanged} />;
  }
  if (closed && !intent && !team) return null;

  const qs = form.questions;
  // ⚠️ ONE COUNT, AND IT IS THE ONE THE SERVER REFUSES ON. Required questions with no value.
  // An optional answer left blank is not outstanding and never appears here.
  const outstanding = qs.filter((q) => q.required && isEmpty(draft[q.id])).length;
  const closesOn = form.closes_at ? closesLabel(form.closes_at) : "";
  const sentOn = intent?.updated_at ? onDay(intent.updated_at) : "";

  return (
    <div className="pform">
      {team && <TeamCard team={team} courseId={courseId} onChanged={onChanged} />}

      <FormHeader name={p.course.name} closed={closed} closesOn={closesOn}
                  count={qs.length} intro={form.intro} />

      {/* 🔴 NO PROGRESS STRIP (owner, 2026-09-09). The handoff specifies one; it counted a
          number the form already shows — every answered card carries the word ANSWERED and a
          jade edge, and the footer carries the count that decides whether this can be sent. A
          bar that restates both is a third place for the same fact to be wrong in.
          ⚠️ ITS "Saved as you go" LABEL WAS NEVER SHIPPED EITHER, for a different reason:
          there is no draft endpoint, and the handoff says not to ship the words without the
          behaviour. Both are gone; the footer is the one place that counts. */}

      {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
      {saved && <p className="alert info" role="status">Saved.</p>}

      {/* 🔴 THE SAME COMPONENT THE TEACHER'S PREVIEW RENDERS. One definition, so what they
          check before opening the form is what a student is asked. */}
      {intent?.teacher_edited_at && <TeacherEdited iso={intent.teacher_edited_at} />}
      <ProjectQuestions form={form} draft={draft} disabled={frozen} note={why} set={set}
                        Q={QCard} Pills={Chips} Other={ZoneChips}
                        classmates={p.classmates} />

      <div className="pf-foot">
        <p className="say" id="pf-why">
          {!mayWrite ? (
            published
              ? "Teams have been published for this cohort and you did not send answers."
              : "This round has closed and you did not send answers."
          ) : outstanding > 0 ? (
            <>
              <b>{outstanding} required question{outstanding === 1 ? "" : "s"} still
                need{outstanding === 1 ? "s" : ""} an answer.</b>
              {" "}Every required question has to be answered before this can be sent.
            </>
          ) : intent ? (
            <>
              <b>Sent{sentOn ? ` ${sentOn}` : ""}.</b>
              {closed
                ? " This round has closed."
                : closesOn
                  ? ` You can change your answers until ${closesOn}.`
                  : " You can change your answers while this round is open."}
            </>
          ) : (
            <>
              <b>All questions answered.</b>
              {closesOn
                ? ` You can send it now and change it until ${closesOn}.`
                : " You can send it now and change it while this round is open."}
            </>
          )}
        </p>
        {mayWrite && (
          // ⚠️ DIMMED, NOT DISABLED — against the handoff, and deliberately. This product's
          // rule is that a greyed control is an affordance and never a gate: the refusal that
          // names the remedy comes from the server, and a client that blocks the click hides
          // it. The intake round behaves the same way. The reason is wired to the button so it
          // is not colour and position alone.
          <button className="pf-send" disabled={busy} aria-describedby="pf-why"
                  onClick={() => void send()}>
            {busy ? "Sending…" : intent ? "Save changes" : "Send my answers"}
          </button>)}
      </div>
    </div>
  );
}

/**
 * The form's own head: its title, its state, and the teacher's intro.
 *
 * 🔴 ONE DEFINITION, TWO SURFACES — the student's form and the teacher's preview of it. The
 * preview is the one screen whose entire claim is "this is what a student sees", and it was
 * rendering a console card headed "Your project" while the student got this. A second copy of
 * a header is a second copy that can drift, on the surface where drift is the whole defect.
 *
 * 🔴 ONE HEADING FOR ONE FORM, AND IT IS THE COHORT'S. The card used to print "Your project"
 * over the intro while the PAGE printed the cohort's name above it — two headings, neither of
 * which needed the other. The page hero has gone with it.
 */
export function FormHeader({ name, closed, closesOn, count, intro }: {
  name: string; closed: boolean; closesOn: string; count: number; intro: string;
}) {
  return (
    <div className="pf-head">
      <div className="pf-headrow">
        <div className="pf-titles">
          <h1>{name}</h1>
          {/* Jade, not brand green: a form's state is a status, and brand green is
              interactive everywhere else in this product. */}
          <span className={closed ? "pf-state shut" : "pf-state"}>
            {closed
              ? `CLOSED${closesOn ? ` · ${closesOn.toUpperCase()}` : ""}`
              : `OPEN${closesOn ? ` · CLOSES ${closesOn.toUpperCase()}` : ""}`}
          </span>
        </div>
        <p className="pf-kick">
          Project preferences · {count} question{count === 1 ? "" : "s"}
        </p>
      </div>
      {/* 🔴 NOTHING AT ALL WHEN THERE IS NO INTRO — no panel, no kicker, no empty box. An
          empty bordered box reads as a broken load. The divider goes with it. */}
      {intro && (
        <div className="pf-intro">
          <div>
            {/* The kicker is the panel's label and is a constant; it is not derived from
                what the teacher wrote. */}
            <p className="k">Read this before you start</p>
            <div className="md"><Markdown text={intro} /></div>
          </div>
        </div>)}
    </div>
  );
}

/**
 * One question, as a card — the student's project form only.
 *
 * 🔴 SEVEN QUESTIONS IN ONE COLUMN READ AS ONE BLOCK OF TEXT. No rule, no number, no edge. A
 * card each, with its index, is what divides them.
 *
 * ⚠️ IT IS NOT `Q`. That primitive dresses the INTAKE round, which is a different form with
 * different questions, and `ProjectQuestions` injects its presentation precisely so the two
 * can differ without the QUESTIONS differing. Widening `Q` instead would have restyled a form
 * this handoff explicitly says it is not about.
 */
export function QCard({ label, hint, optional = false, index, answered = false, note = "",
                 children }: {
  label: string; hint: string; optional?: boolean; index?: number;
  answered?: boolean; note?: string; children: React.ReactNode;
}) {
  return (
    <div className={`pq${answered ? " done" : ""}`}>
      <div className="pq-head">
        {index !== undefined && (
          <span className="pq-n">{String(index).padStart(2, "0")}</span>)}
        <div className="pq-body">
          {/* ⚠️ A `<label>`, NOT A HEADING. There is one `h1` on this page — the cohort's
              name — and a question is the name of a control, not a section of the document. */}
          <label className="pq-label">
            {label}
            {/* Nothing marks a required question: required is the default, and an asterisk on
                five of seven is noise. The footer carries the count. */}
            {optional && <span className="pq-opt">OPTIONAL</span>}
          </label>
          <p className="pq-hint">{hint}</p>
        </div>
        {/* 🔴 THE WORD, NOT THE JADE EDGE. The edge is the same fact for somebody who can see
            it; this is the same fact for somebody who cannot. */}
        {answered && <span className="pq-done">ANSWERED</span>}
      </div>
      {children}
      {note && <p className="pq-note">{note}</p>}
    </div>
  );
}

/**
 * A single choice, as real radios.
 *
 * 🔴 A RADIO GROUP, NOT A ROW OF PRESSED BUTTONS. `aria-pressed` buttons are a toggle group:
 * each is its own tab stop and each is announced "pressed / not pressed". These four options
 * are mutually exclusive, so the right semantics are one tab stop, arrow keys within it, and
 * "2 of 4" — which is what a native radio group gives for free. The shape is drawn; the
 * semantics are not changed.
 *
 * ⚠️ THE INTAKE ROUND'S `Pills` IS UNTOUCHED. It is a different form and a different handoff,
 * and `ProjectQuestions` takes its primitives as props for exactly this reason.
 */
export function Chips({ options, value, disabled, label, onChange }: {
  options: string[]; value: string; disabled: boolean; label?: string;
  onChange: (v: string) => void;
}) {
  // ⚠️ THE GROUP NEEDS A STABLE `name` OR TWO QUESTIONS ON ONE PAGE SHARE A SELECTION. It is
  // derived from the question's own label, which is unique within a form the server validated.
  const group = `q-${(label ?? "choice").replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className="pf-chips" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <label key={o} className="pf-chip">
          <input type="radio" name={group} value={o} checked={value === o}
                 disabled={disabled} onChange={() => onChange(o)} />
          <span>{o}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * A time zone: the known ones as chips, plus `Other…` for one somebody types.
 *
 * 🔴 "Other" IS A CHOICE THE FORM OFFERS, so the page has to offer it — and it is the one
 * answer here with no derivable offset, which the server records as unknown rather than
 * guessing zero. The typed box is bounded by the server's own `other_max`, so it stops where
 * the validator refuses rather than letting somebody write past it and be told on submit.
 */
export function ZoneChips({ label, hint, options, value, disabled, index, answered = false,
                    note = "", other_max = 64, onChange }: {
  label: string; hint: string; options: string[]; value: string; disabled: boolean;
  zone?: boolean; index?: number; answered?: boolean; note?: string; other_max?: number;
  onChange: (v: string) => void;
}) {
  const known = options.includes(value);
  const typed = !known && value !== "";
  const group = `q-${label.replace(/\W+/g, "-").toLowerCase()}`;
  const left = other_max - value.trim().length;
  return (
    <QCard label={label} hint={hint} index={index} answered={answered} note={note}>
      <div className="pf-chips" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <label key={o} className="pf-chip zone">
            <input type="radio" name={group} value={o} checked={value === o}
                   disabled={disabled} onChange={() => onChange(o)} />
            <span>{o}</span>
          </label>
        ))}
        <label className="pf-chip zone other">
          <input type="radio" name={group} checked={typed} disabled={disabled}
                 onChange={() => onChange(" ")} />
          <span>Other…</span>
        </label>
      </div>
      {typed && (
        <>
          <input className="pf-url" value={value.trim()} maxLength={other_max}
                 disabled={disabled} aria-label={`${label}: your own answer`}
                 onChange={(e) => onChange(e.target.value)} />
          {/* Only when it is nearly spent — a counter that is always there is noise. */}
          {left < 20 && (
            <p className="pq-hint" aria-live="polite">{left} characters left.</p>)}
        </>
      )}
    </QCard>
  );
}

/**
 * What somebody sees instead of the form when they cannot fill it in.
 *
 * Design handoff 04, option `5c-3`. The prototype's own logo is dropped: the applicant shell
 * already carries the brand band, and two wordmarks on one screen is not what it asked for.
 * The cohort name stays in the hero above — the isolated prototype could not name the cohort
 * a reader is being refused from, and somebody sent several links needs to know which.
 *
 * 🔴 ONE OF THE FOUR CAUSES IS THEIRS TO FIX AND THREE ARE NOT, and that decides whether
 * there is a button. `not_on_roster` means they signed in with an account this cohort does
 * not list; `prompt=select_account` is already set, so the remedy is one press. A missing
 * scope, a roster nobody has synced and a roster Classroom returned no names for are all
 * somebody else's, and the handoff says so in the same words `api.ts` does: offering that
 * button for those "would send somebody round a loop that cannot work".
 *
 * 🔴 NO "APPLY INSTEAD" (owner's call, 2026-09-09), against the prototype, which offered it
 * as a second exit for "applied but not enrolled yet". It points at the INTAKE round, which
 * on a cohort that is already running is usually shut — so the common outcome is a second
 * refusal, from a different form, for a different reason. That case is carried in prose
 * instead, where it costs nobody a wasted click.
 *
 * 🔴 THE SENTENCE IS THE SERVER'S, which is the handoff's instruction and this codebase's
 * rule: it is the only copy that cannot drift from what was actually enforced. Only the
 * `Refused: ` prefix is dropped, because the eyebrow above it already carries that job — on
 * the wire it stays, since the same string is rendered by `humanError` on the PUT path where
 * the prefix is what makes the sentence findable.
 *
 * ⚠️ THE ADDRESS IS ON ITS OWN LINE, where the prototype has it inline mid-sentence. Inline
 * would mean the client composing the sentence around it, and then two files describe one
 * refusal. On its own line it is also the first thing the eye lands on, which is right when
 * the wrong address IS the fault.
 *
 * ⚠️ `role="status"` ON THE SENTENCE, NOT `role="alert"` ON THE PANEL. The handoff's
 * accessibility note reads "refusals are role=alert; success notices role=status — both
 * already correct in the source, preserve them", and what the source had here was `status`,
 * deliberately: three of the four causes are nothing to do with the reader, and announcing
 * them as an error tells a student something went wrong with their account. The panel itself
 * takes no live-region role at all — it is the page's own content at load, not a
 * notification, and `alert` on it would interrupt a screen reader with the whole panel.
 */
function NotEligible({ eligibility, courseId, email, demo, onJoined }: {
  eligibility: NonNullable<ApplyCohortView["project"]>["eligibility"];
  courseId: string; email: string;
  /** A cohort this console invented, whose roster is ours to write. */
  demo: boolean;
  onJoined: () => void;
}) {
  const [joining, setJoining] = useState(false);
  const [joinErr, setJoinErr] = useState<ApiError | null>(null);
  const join = async () => {
    setJoining(true); setJoinErr(null);
    try { await joinDemoCohort(courseId); onJoined(); }
    catch (e) { setJoinErr(e as ApiError); }
    finally { setJoining(false); }
  };
  const theirs = eligibility.cause === "not_on_roster";
  // The eyebrow says which kind of refusal this is, so the sentence does not have to.
  const kick = theirs ? "Signed in · not recognised" : "Signed in · cohort not ready";
  // ⚠️ RE-CAPITALISE AFTER STRIPPING. `Refused: the Google account…` becomes `the Google
  // account…` — a sentence starting lower case, which reads as a fragment somebody forgot to
  // finish. Caught by rendering it, not by reading it.
  const bare = eligibility.reason.replace(/^Refused:\s*/, "");
  const said = bare.charAt(0).toUpperCase() + bare.slice(1);

  return (
    // 🔴 THE SAME SHELL AS THE FIVE DOORS, AND DELIBERATELY WITHOUT THE SCANLINE — "this is a
    // refusal, not a door". Before this the six entrances were three different layouts.
    // ⚠️ THREE THINGS HERE ARE THE OWNER'S CALL AND THE UPDATED HANDOFF REVERSES ALL THREE.
    // They are kept: the heading is "You are not enrolled on this cohort" and not "not on this
    // cohort's roster" (a roster is our word for it, enrolment is theirs); there is no
    // "Apply instead" second exit (it points at the INTAKE round, usually shut on a cohort
    // already running, so the common outcome is a second refusal from a different form for a
    // different reason); and the live-region role stays `status` on the sentence rather than
    // `alert` on the panel, because three of the four causes are nothing to do with the reader
    // and `alert` would interrupt a screen reader with the whole panel. The handoff's own
    // accessibility section says to preserve what the source has, which contradicts its
    // screen-3 spec — this follows the former.
    <Panel scan={false}>
      <div>
        <p className={`panel-eyebrow${theirs ? " warn" : ""}`}>{kick}</p>
        <h2 className="tight">{theirs
          ? "You are not enrolled on this cohort"
          : "This cohort is not ready for you yet"}</h2>
        {theirs && <p className="panel-para"><span className="addr">
          {email || "an account with no address"}</span></p>}
        <p className="panel-para soft" role="status" style={{ marginTop: 10 }}>{said}</p>
        <p className="panel-para quiet" style={{ marginTop: 10 }}>
          {theirs
            // ⚠️ ONLY THE CASE THE SERVER'S SENTENCE CANNOT COVER. The prototype's second
            // paragraph opens "if you are enrolled under a different address, sign in with
            // that one" — which is the remedy the server sentence already gives, in other
            // words, two paragraphs apart. Saying it twice is how a reader starts skimming.
            ? "If you have applied but have not been enrolled yet, this form is not open to "
              + "you until your teacher adds you to the cohort."
            : "There is nothing wrong with your account and signing in again will not change "
              + "this. Your teacher fixes it from the console."}
        </p>
      </div>

      {/* 🔴 A BUTTON ONLY WHERE PRESSING IT CAN WORK. `not_on_roster` means "you signed in
          with the wrong account", and the remedy is one link. For `no_scope`, `never_synced`
          and `no_names` the same button sends somebody round a loop that cannot work — twice
          — before they conclude the fault is theirs. */}
      {theirs && (
        <div className="panel-acts">
          <a className="panel-act" href={APPLY_SIGN_IN_URL(courseId)}>
            Use a different account</a>
          {/* 🔴 ONLY ON A COHORT THIS CONSOLE INVENTED, where the roster is ours to write. On a
              real cohort the roster is Classroom's, and a button here would be an enrolment
              nobody made. Whether THIS person may join is the server's answer — it refuses
              with a sentence rather than the button being hidden on a guess. */}
          {demo && (
            <button className="panel-act" type="button" disabled={joining}
                    onClick={() => void join()}>
              {joining ? "Joining…" : "Join this demo cohort"}</button>)}
        </div>)}
      {joinErr && (
        <p className="alert refused" role="alert" style={{ margin: "12px 0 0" }}>
          ⛔ {joinErr.message}</p>)}

      {/* 🔴 THE FORM IS NOT RENDERED AT ALL (owner's call, 2026-09-09, twice). It was
          behind a closed disclosure on the argument that somebody kept out may reasonably
          want to know what is asked. The owner disagrees, and it is their call: the questions
          are a cohort's own configuration, and showing them to somebody the roster does not
          contain publishes them to anyone with a Google account and a link. The refusal is
          the whole page now.
          ⚠️ IT ALSO FIXED THE LAYOUT. [X] The disclosure's contents were wider than the panel
          and broke out of it, which is what "the width looks strange" was. */}
    </Panel>
  );
}

/** A row of choices, exactly the ones this server accepts. */
export function Pills({ options, value, disabled, onChange }: {
  options: string[]; value: string; disabled: boolean; onChange: (v: string) => void;
}) {
  return (
    <span className="row gap wrap">
      {options.map((o) => (
        <button key={o} type="button" disabled={disabled}
                className={value === o ? "pill sm on" : "pill sm"}
                aria-pressed={value === o}
                onClick={() => onChange(o)}>{o}</button>
      ))}
    </span>
  );
}

/**
 * 🔴 HOW SOMEBODY IS REACHED AND WHERE THEIR WORK LIVES: a role answer or a link. These stay
 * editable for somebody who already applied after the form closes — `applications.contact` on
 * the server, which decides again on save.
 */
function isContact(q: IntakeQuestion): boolean {
  return Boolean(q.role) || q.kind === "link";
}

/**
 * Is this answer complete, by the server's own rule?
 *
 * 🔴 A GRID IS ONE ANSWER AND IS COMPLETE ONLY WHEN EVERY ROW HAS A LEVEL. The server refuses a
 * partial grid — a missing row is not the lowest level — so a half-filled one must not read as
 * done.
 */
export function isAnswered(q: IntakeQuestion, v: ApplicationAnswers[string] | undefined):
    boolean {
  if (q.kind === "matrix") {
    const grid = v && typeof v === "object" ? v : {};
    return (q.rows ?? []).length > 0 && (q.rows ?? []).every((r) => Boolean(grid[r]));
  }
  if (q.kind === "scale") return typeof v === "number";
  return typeof v === "string" && v.trim() !== "";
}

/**
 * What a save sends: an answer to every question this form asks, and nothing else.
 *
 * ⚠️ NOT THE STORED ANSWERS WHOLE. An answer to a question the form no longer asks is kept on
 * the server — putting the question back makes it readable again — and sending it would be
 * refused as "not a question this form asks".
 */
export function answersFor(questions: IntakeQuestion[], draft: ApplicationAnswers):
    ApplicationAnswers {
  return Object.fromEntries(questions.map((q) => {
    const v = draft[q.id];
    if (q.kind === "matrix") return [q.id, v && typeof v === "object" ? { ...v } : {}];
    if (q.kind === "scale") return [q.id, typeof v === "number" ? v : ""];
    return [q.id, typeof v === "string" ? v : ""];
  }));
}

/**
 * `"Junior: 1-2 years"` → `Junior` over `1–2 yr`.
 *
 * ⚠️ A DISPLAY TRANSFORM, NOT A VOCABULARY CHANGE. The level a teacher wrote is what gets
 * submitted; the years qualifier is what an applicant is actually choosing on, so it earns the
 * second line.
 */
function levelLines(label: string): [string, string] {
  const i = label.indexOf(":");
  if (i < 0) return [label, ""];
  const tail = label.slice(i + 1).trim()
    .replace(/-/g, "–")
    .replace(/\byears?\b/, "yr");
  return [label.slice(0, i).trim(), tail];
}

const DISCORD_ID = /^\d{17,19}$/;

type IntakeView = ApplyCohortView & { form: NonNullable<ApplyCohortView["form"]> };

function CohortView({ view, courseId, email, onChanged }: {
  view: IntakeView; courseId: string; email: string; onChanged: () => void;
}) {
  const { form, application, status } = view;

  const [draft, setDraft] = useState<ApplicationAnswers>(() =>
    application ? { ...application.answers } : {});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);

  // 🔴 PUBLISHED TEAMS LOCK NOTHING HERE (2026-09-22). Only the form being shut fixes these
  // answers, and the server says the same. A contact answer — a role or a link — stays
  // editable for somebody who already applied, which is the whole point of the rule.
  const closed = form.state !== "open";
  const locked = (q: IntakeQuestion) => closed && !(application !== null && isContact(q));
  const everything = form.questions.every(locked);

  const set = (id: string, v: ApplicationAnswers[string]) => {
    setDraft((d) => ({ ...d, [id]: v }));
    setSaved(false);
  };

  const submit = useCallback(async () => {
    setBusy(true); setErr(null); setSaved(false);
    try {
      await putApplication(courseId, answersFor(form.questions, draft));
      setSaved(true);
      onChanged();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  }, [courseId, draft, form.questions, onChanged]);

  const withdraw = useCallback(async () => {
    setBusy(true); setErr(null);
    try { await withdrawApplication(courseId); onChanged(); }
    catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  }, [courseId, onChanged]);

  // ── the rail is DERIVED, recomputed from `draft` on every keystroke. No state, no effect,
  //    no debounce: it is a projection of the answers, and storing it would let the two drift.
  // 🔴 ONLY REQUIRED QUESTIONS COUNT. An optional one is out of the denominator — counting it
  // would tell somebody they were unfinished where the server would accept them.
  const required = form.questions.filter((q) => q.required);
  const done = (q: IntakeQuestion) => isAnswered(q, draft[q.id]);
  const answered = required.filter(done).length;
  const left = required.filter((q) => !done(q));

  const lockNote = closed ? "Applications have closed, so these are read-only now." : "";

  return (
    <>
      <section className="apply-hero">
        <div className="apply-eyebrow">
          {status
            ? <StatusBadge status={status} state={form.state} />
            : <span className="kick">Application</span>}
          {/* `closes_at` is optional server-side: no date, no rule and no label. */}
          {form.closes_at && <>
            <span className="rule" />
            {/* ⚠️ `closesLabel`, NOT `When`. A deadline is a moment, and this says which zone
                it is read in. */}
            <span className="closes">Closes {closesLabel(form.closes_at)}</span>
          </>}
        </div>
        <h2>{view.course.name}</h2>
        {/* Through `Markdown`, which builds React nodes and has no HTML path at all. */}
        {form.intro && <div className="apply-intro"><Markdown text={form.intro} /></div>}
      </section>

      <div className="apply-body">
        <div className="stack">
          {/* 🔴 BOTH ROUNDS ON ONE PAGE, NOT A CHOOSER. A student does not know this console
              has rounds; they know their teacher sent them a link. The project card renders
              itself away when this cohort has no project form. */}
          {view.project && (
            <ProjectSection p={view.project} courseId={courseId} email={email}
                            demo={view.demo} onChanged={onChanged} />)}
          {closed && !application && (
            <Card><CardBody>
              <p className="alert info" role="status">
                {form.state === "closed"
                  ? "Applications for this cohort have closed."
                  : "This cohort is not taking applications."}
              </p>
            </CardBody></Card>)}
          {application?.loaded_at && <TeacherLoaded iso={application.loaded_at} />}
          {application?.teacher_edited_at && (
            <TeacherEdited iso={application.teacher_edited_at} />)}
          {(!closed || application) && (
            <ApplicationQuestions questions={form.questions} draft={draft} set={set}
                                  locked={locked} note={lockNote} />)}
        </div>

        {(!closed || application) && (
          <aside className="rail">
            <div className="rail-kick">Your application</div>

            <div>
              {/* A screen-reader user hears the count change as they answer. */}
              <div className="rail-count" aria-live="polite">
                <span className="rail-num">{answered}</span>
                <span className="rail-cap">of {required.length} required answered</span>
              </div>
              <div className="rail-bar"
                   style={{ gridTemplateColumns: `repeat(${Math.max(required.length, 1)}, minmax(0,1fr))` }}>
                {required.map((q) => (
                  <span key={q.id} className={done(q) ? "rail-seg on" : "rail-seg"} />))}
              </div>
            </div>

            {/* ⚠️ WHAT IS STILL TO ANSWER, BY NAME — the questions are a teacher's, so there
                are no fixed sections to count by. Each is a real link to its question. */}
            {left.length > 0 && (
              <div className="rail-list">
                {left.map((q) => (
                  <a className="rail-row todo" key={q.id} href={`#q-${q.id}`}>
                    <span className="rail-mark todo">·</span>
                    {q.label}
                  </a>))}
              </div>)}

            {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
            {saved && !err && (
              <p className="alert info" role="status">
                Saved. You can come back and change this until applications close.
              </p>)}

            <div className="rail-send">
              {everything ? (
                <p className="rail-help">
                  {lockNote || "This application is read-only."}
                </p>
              ) : (<>
                {/* 🔴 GREYED IS AN AFFORDANCE, NOT A GATE. The click always goes through when
                    the page would have allowed it before, because the server's refusal is
                    what names the remedy — a client that blocks silently hides that sentence. */}
                <button className={answered < required.length ? "rail-btn waiting" : "rail-btn"}
                        disabled={busy}
                        onClick={() => void submit()}>
                  {busy ? "Saving…" : application ? "Save changes" : "Send application"}
                </button>
                <p className="rail-help">
                  {answered < required.length
                    ? <>{required.length - answered} still to answer. You can change every
                        answer until applications close.</>
                    : <>Everything required is answered. You can change every answer until
                        applications close.</>}
                </p>
              </>)}

              {application && !application.withdrawn && !closed && (
                <button className="rail-ghost" disabled={busy}
                        onClick={() => void withdraw()}>Withdraw</button>)}

              {application && (
                <div className="rail-stamp">
                  Sent <When iso={application.submitted_at} />
                  {application.updated_at !== application.submitted_at && <>
                    <br />last changed <When iso={application.updated_at} /></>}
                </div>)}
            </div>
          </aside>)}
      </div>
    </>
  );
}

/**
 * The application form's questions — the ONE renderer, used by the applicant's own page, a
 * teacher's editor and the teacher's preview, so all three offer exactly the same choices.
 *
 * 🔴 IT DRAWS WHAT THE SERVER SENDS. Since 2026-10-13 the questions are a teacher's to write,
 * so this file knows six SHAPES — text, a link, one of a list, a time zone, a grid and a scale
 * — and nothing about what any of them asks.
 * ⚠️ WHAT IS FIXED IS DECIDED BY THE CALLER, per question, and only drawn here: a contact
 * answer stays editable after the form shuts for somebody who already applied. The server
 * decides again on save.
 */
export function ApplicationQuestions({ questions, draft, set, locked, note }: {
  questions: IntakeQuestion[];
  draft: ApplicationAnswers;
  set: (id: string, v: ApplicationAnswers[string]) => void;
  locked: (q: IntakeQuestion) => boolean;
  /** Why these answers are fixed, when they are; shown above the first question. */
  note: string;
}) {
  return (
    <div className="apply-qs">
      <div className="band" id="sec-0">
        <div className="band-kick">
          {questions.length} question{questions.length === 1 ? "" : "s"}
        </div>
        <div className="band-title">Your application</div>
        {note && <p className="band-note">{note}</p>}
      </div>
      <div className="qgroup roomy">
        {questions.map((q) => (
          <IntakeQ key={q.id} q={q} value={draft[q.id]} disabled={locked(q)}
                   onChange={(v) => set(q.id, v)} />))}
      </div>
    </div>
  );
}

/** One question, drawn by its kind. */
function IntakeQ({ q, value, disabled, onChange }: {
  q: IntakeQuestion; value: ApplicationAnswers[string] | undefined; disabled: boolean;
  onChange: (v: ApplicationAnswers[string]) => void;
}) {
  const text = typeof value === "string" ? value : "";
  const anchor = `q-${q.id}`;
  if (q.kind === "timezone") {
    return <Other anchor={anchor} label={q.label} hint={q.hint} zone
                  options={q.options ?? []} value={text} disabled={disabled}
                  optional={!q.required} onChange={onChange} />;
  }
  if (q.kind === "choice" && q.other) {
    return <Other anchor={anchor} label={q.label} hint={q.hint} options={q.options ?? []}
                  value={text} disabled={disabled} optional={!q.required}
                  onChange={onChange} />;
  }
  return (
    <Q anchor={anchor} label={q.label} hint={q.hint} optional={!q.required}>
      {q.kind === "choice" ? (
        // A row of pills, because the options are meant to be compared side by side.
        <div className="pills">
          {(q.options ?? []).map((o) => (
            <button key={o} type="button" disabled={disabled}
                    className={text === o ? "pill on" : "pill"}
                    aria-pressed={text === o}
                    onClick={() => onChange(o)}>{o}</button>
          ))}
        </div>
      ) : q.kind === "matrix" ? (
        <Grid q={q} value={value && typeof value === "object" ? value : {}}
              disabled={disabled} onChange={onChange} />
      ) : q.kind === "scale" ? (
        <div className="scale">
          {scale(q.lowest ?? 1, q.highest ?? 5).map((n) => (
            <button key={n} type="button" disabled={disabled}
                    className={value === n ? "pill num on" : "pill num"}
                    aria-pressed={value === n}
                    aria-label={n === (q.lowest ?? 1) && q.low ? `${n}, ${q.low}`
                      : n === (q.highest ?? 5) && q.high ? `${n}, ${q.high}` : String(n)}
                    onClick={() => onChange(n)}>{n}</button>
          ))}
        </div>
      ) : q.kind === "link" ? (
        <input className="tin mono" type="url" value={text} maxLength={2000}
               placeholder="https://" disabled={disabled}
               onChange={(e) => onChange(e.target.value)} />
      ) : q.role === "discord_user_id" ? (
        <DiscordId value={text} disabled={disabled} onChange={onChange} />
      ) : (q.max ?? 500) > 256 ? (
        // ⚠️ `maxLength` IS THE SERVER'S NUMBER, so the box stops where the validator refuses.
        <textarea className="ta" rows={3} value={text} maxLength={q.max ?? 500}
                  disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className={q.role === "discord_username" ? "tin mono" : "tin"} value={text}
               maxLength={q.max ?? 256} disabled={disabled}
               onChange={(e) => onChange(e.target.value)} />
      )}
    </Q>
  );
}

/**
 * A grid: one level per row, from one list.
 *
 * 🔴 A RADIO GRID, WITH A REAL `name` PER ROW. The rows look like buttons and the CONTROLS are
 * still radios: buttons would be unreachable from a keyboard, and a screen reader would read
 * twenty unlabelled controls instead of four questions with five answers each.
 */
function Grid({ q, value, disabled, onChange }: {
  q: IntakeQuestion; value: Record<string, string>; disabled: boolean;
  onChange: (v: Record<string, string>) => void;
}) {
  return (
    <div className="langs">
      {(q.rows ?? []).map((row, i) => (
        <div className="langrow" key={row}>
          <div className="langrow-label">{row}</div>
          <div className="langopts">
            {(q.levels ?? []).map((level) => {
              const [head, yrs] = levelLines(level);
              return (
                <label className="langopt" key={level}>
                  <input type="radio" name={`grid-${q.id}-${i}`}
                         aria-label={`${row}: ${level}`} disabled={disabled}
                         checked={value[row] === level}
                         onChange={() => onChange({ ...value, [row]: level })} />
                  <span>{head}{yrs && <span className="yrs">{yrs}</span>}</span>
                </label>);
            })}
          </div>
        </div>))}
    </div>
  );
}

/** The Discord snowflake, with its shape checked as it is typed.
 *  ⚠️ AFFORDANCE ONLY: the server is the authority and its refusal is what is shown on save. */
function DiscordId({ value, disabled, onChange }: {
  value: string; disabled: boolean; onChange: (v: string) => void;
}) {
  const ok = DISCORD_ID.test(value);
  const digits = value.replace(/\D/g, "").length;
  return (
    <div className="tin-row">
      <input className={`tin mono${ok ? " ok" : value ? " bad" : ""}`} value={value}
             maxLength={32} inputMode="numeric" disabled={disabled}
             onChange={(e) => onChange(e.target.value)} />
      {value !== "" && (
        <span className={ok ? "tin-note ok" : "tin-note bad"}>
          {ok ? `${digits} DIGITS — OK` : "NOT 17–19 DIGITS"}
        </span>)}
    </div>
  );
}

/** ⚠️ ANSWERS A TEACHER LOADED FROM A FILE ARE NOT THE APPLICANT'S TYPING, and say so. The day
 *  only, never who — the rule `TeacherEdited` set. */
function TeacherLoaded({ iso }: { iso: string }) {
  const day = new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return (
    <p className="alert info" role="status">
      Your teacher loaded these answers for you on {day}. Check them, and change anything that
      is not right.
    </p>
  );
}

/**
 * 🔴 ANSWERS SOMEBODY ELSE REWROTE SHOULD NOT LOOK LIKE YOUR OWN TYPING (owner, 2026-09-22).
 * The day and nothing else: never which teacher, and never what an answer was before — both are
 * in the console's own log, which no student reads.
 */
function TeacherEdited({ iso }: { iso: string }) {
  const day = new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return (
    <p className="alert info" role="status">
      Your teacher updated some of these answers on {day}.
    </p>
  );
}

/**
 * The team, once a teacher has published one.
 *
 * 🔴 ANYBODY ON THE TEAM MAY NAME IT, AND THE CALL CARRIES NO TEAM. The server renames the team
 * this session is on, so the page has nothing to send that could point at somebody else's.
 */
function TeamCard({ team, courseId, onChanged }: {
  team: NonNullable<NonNullable<ApplyCohortView["project"]>["team"]>;
  courseId: string; onChanged: () => void;
}) {
  const [naming, setNaming] = useState(false);
  return (
    <Card>
      <CardHead title={team.name || `Team ${team.number}`}
                desc="Your team. Say hello — you are building together from here."
                right={!naming && (
                  <button className="btn sm" type="button" onClick={() => setNaming(true)}>
                    {team.name ? "Rename" : "Name your team"}
                  </button>)} />
      <CardBody>
        {naming && (
          <TeamNameForm number={team.number} name={team.name}
                        hint={"Everyone on your team sees it, and so does your teacher, who "
                              + "can change it."}
                        onSave={async (name) => { await renameMyTeam(courseId, name); onChanged(); }}
                        onClose={() => setNaming(false)} />)}
        {team.teammates.length === 0 ? (
          <p className="muted">You are the only person in this team.</p>
        ) : (
          <ul className="applist">
            {team.teammates.map((m, i) => (
              <li key={i} className="mate">
                <strong>{m.display_name}</strong>
                {/* ⚠️ ABSENT, NOT EMPTY. The form requires a Discord handle, so this is
                    normally present — the key is omitted only for a row written before it
                    was required, and an empty string would read as one they typed wrong. */}
                {/* 🔴 ONLY WHAT THE TEACHER MARKED SHAREABLE. It used to be two hardcoded
                    answers — the domain and the one-line idea, which is why these people are
                    on a team together. A form somebody writes could ask anything, so what a
                    classmate sees is a decision per question and it defaults OFF. Never
                    anybody's judgement of them: that is a join away and not on this payload
                    at all. */}
                {m.shared.map((x) => (
                  <span key={x.q} className="small">{x.a}</span>))}
                {m.discord_username
                  ? <span className="small">Discord: {m.discord_username}</span>
                  : <span className="small muted">no Discord username on file</span>}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function StatusBadge({ status, state }: {
  status: ApplyCohortView["status"]; state: FormState;
}) {
  if (status === "team_assigned") return <Badge tone="ok">in a team</Badge>;
  if (status === "not_assigned") {
    return <Badge tone="warn" title="Teams were published and you are not in one. Ask your
                                     teacher.">not in a team</Badge>;
  }
  if (status === "withdrawn") return <Badge tone="held">withdrawn</Badge>;
  if (status === "received") return <Badge tone="ok">received</Badge>;
  return <Badge tone="plain">{state === "open" ? "open" : "closed"}</Badge>;
}

function scale(lo: number, hi: number): number[] {
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
}

/** A choice that also allows "Other", exactly as the form does.
 *
 * ⚠️ "Other" IS A CHOICE THE FORM OFFERS, so the page has to offer it too — and the two boxes
 * built this way are the only free text on the whole submission that a model ever reads.
 */
export function Other({ label, hint, options, value, disabled, zone = false,
                       optional = false, anchor, onChange }: {
  label: string; hint: string; options: string[]; value: string; disabled: boolean;
  zone?: boolean; optional?: boolean; anchor?: string; onChange: (v: string) => void;
}) {
  const known = options.includes(value);
  const [own, setOwn] = useState(known ? "" : value);
  // Zone codes are strings a person reads character by character, so they are mono — the same
  // rule this stylesheet already applies to ids, hashes and URLs.
  const cls = zone ? "pill zone" : "pill sm";
  return (
    <Q label={label} hint={hint} optional={optional} anchor={anchor}>
      <div className="pills">
        {options.map((o) => (
          <button key={o} type="button" disabled={disabled}
                  className={value === o ? `${cls} on` : cls} aria-pressed={value === o}
                  onClick={() => onChange(o)}>{o}</button>
        ))}
        <button type="button" disabled={disabled} aria-pressed={!known && value !== ""}
                className={!known && value !== "" ? `${cls} other on` : `${cls} other`}
                onClick={() => onChange(own || " ")}>Other…</button>
      </div>
      {!known && value !== "" && (
        <input className="tin" value={own || value.trim()} maxLength={64}
               disabled={disabled} aria-label={`${label}: your own answer`}
               onChange={(e) => { setOwn(e.target.value); onChange(e.target.value); }} />
      )}
    </Q>
  );
}

/** One question. Deliberately not `Field`: that one is built for a settings row with a
 *  rationale popover, and this is a form somebody fills in once. */
export function Q({ label, hint, optional = false, anchor, children }: {
  label: string; hint: string; optional?: boolean;
  /** The id a rail link points at, so "still to answer" can take you there. */
  anchor?: string; children: React.ReactNode;
}) {
  return (
    <div className="aq" id={anchor}>
      <label className="aq-label">
        {label}
        {/* ⚠️ AN OPTIONAL ANSWER, SAID ON THE LABEL — the same fact the rail's
            denominator leaves out. */}
        {optional && <span className="aq-opt">OPTIONAL</span>}
      </label>
      <p className="aq-hint">{hint}</p>
      {children}
    </div>
  );
}

/** The student shell. No sidebar and no cohort picker — there is one cohort here, and it is
 *  the one in the URL. */
export function ApplyShell({ who, onSignOut, children }: {
  who?: ApplyMe; onSignOut?: () => void; children: React.ReactNode;
}) {
  return (
    <div className="apply">
      {/* 🔴 THE BAND IS DARK IN BOTH THEMES, AND THAT IS A CONSTRAINT RATHER THAN A TASTE.
          The supplied wordmark is white with a #00FF99 "3" and has no dark-on-light variant,
          so this is the only ground it reads correctly on. Everything BELOW follows the
          toggle, so light mode is still a real light mode. */}
      <header className="apply-brand">
        <img className="logo" src="/brand/turbin3-logo.svg" alt="Turbin3" width={177} height={35} />
        <span className="spacer" />
        {who && <>
          <span className="who">{who.display_name}</span>
          <button className="btn" onClick={onSignOut}>Sign out</button>
        </>}
        <ThemeToggle />
      </header>
      {/* 🔴 AND HERE MOST OF ALL. A teacher who meets a white screen can open devtools; an
          applicant on a phone cannot, and has no way to tell a broken page from a form that
          simply refuses them. */}
      <ErrorBoundary>{children}</ErrorBoundary>
    </div>
  );
}
